import React from "react";
import { compose, withProps } from "recompose";
import { DocumentRenderer } from "components";
import { Trans } from "react-i18next";
import { Table } from "reactstrap";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import ContactDetails from "./ContactDetails";
import HeaderData from "./HeaderData";
import { withSelectedOrganisation } from "../../components/withSelectedOrganisation";
import { formatAccountCurrency } from "../../services/util";

export const StatementDocument = ({
  grouped,
  contact,
  logo,
  date,
  organisation,
  organisationData,
  settings,
  t,
  ...props
}) => (
  <DocumentRenderer {...props}>
    <div className="container-fluid m-0 p-0">
      {settings.logo ? (
        <div className="mb-4 d-flex justify-content-end">
          <img style={{ maxHeight: 150 }} src={settings.logo} alt="logo" />
        </div>
      ) : null}
      <div className="d-flex justify-content-between mb-5">
        <div>
          <h1>
            <Trans>Statement</Trans>
          </h1>
          <ContactDetails t={t} contact={contact} />
        </div>
        <div className="d-flex align-items-start">
          <div className="mr-4">
            <HeaderData className="mb-1" label="Statement Date" value={date} />
          </div>
          <div>
            {settings.header ? (
              <div style={{ whiteSpace: "pre-line" }}>{settings.header}</div>
            ) : (
              <ContactDetails t={t} contact={organisationData} />
            )}
          </div>
        </div>
      </div>
      {grouped.map((i) => (
        <div key={i.currency}>
          <div className="row mb-5">
            <div className="col">
              <h1>
                <Trans>STATEMENT</Trans>
              </h1>
              <h2>{contact.Name}</h2>
              <small>
                <Trans>As at {{ date }}</Trans>
              </small>
            </div>
            <div className="col">
              {logo || props.selectedOrganisation.logo ? (
                <img
                  className="float-right"
                  alt="Logo"
                  style={{ maxHeight: 150 }}
                  src={logo || props.selectedOrganisation.logo}
                />
              ) : null}
            </div>
          </div>
          <h5>
            <Trans>Balance due in {{ currency: i.currency }}</Trans>
          </h5>
          <Table className="table table-sm small" responsive>
            <thead>
              <tr>
                <th>
                  <Trans>Date</Trans>
                </th>
                <th>
                  <Trans>Activity</Trans>
                </th>
                <th>
                  <Trans>Reference</Trans>
                </th>
                <th>
                  <Trans>Due Date</Trans>
                </th>
                <th className="text-right">
                  <Trans>Invoice Amount</Trans>
                </th>
                <th className="text-right">
                  <Trans>Payments</Trans>
                </th>
                <th className="text-right">
                  <Trans>Balance</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {i.data.map((j, idx) => (
                <tr key={idx}>
                  <td>{j.DateString}</td>
                  <td>
                    {j.isInvoice
                      ? t("Invoice #{{ number }}", { number: j.InvoiceNumber })
                      : null}
                    {j.isCreditNote
                      ? t("Credit Note #{{ number }}", {
                          number: j.CreditNoteNumber,
                        })
                      : null}
                  </td>
                  <td>{j.Reference}</td>
                  <td>{j.DueDateString}</td>
                  <td className="text-right">
                    {formatAccountCurrency(j.Total)}
                  </td>
                  <td className="text-right">
                    {formatAccountCurrency(j.Payments)}
                  </td>
                  <td className="text-right">
                    {formatAccountCurrency(j.Remaining)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan="6" className="text-right">
                  <Trans>BALANCE DUE {{ currency: i.currency }}</Trans>
                </th>
                <th className="text-right">
                  {formatAccountCurrency(i.balanceDue)}
                </th>
              </tr>
            </tfoot>
          </Table>
          <footer>
            <hr />
            <div className="row mt-5">
              <div className="col">
                <h1>
                  <Trans>PAYMENT ADVICE</Trans>
                </h1>
                <small>
                  <Trans>To: {{ Name: contact.Name }}</Trans>
                </small>
              </div>
              <div className="col">
                {/* aed update table to Table reastStrap */}
                <Table className="table table-small small" responsive>
                  <tbody>
                    <tr>
                      <td>
                        <Trans>Customer</Trans>
                      </td>
                      <td>{organisation}</td>
                      <td />
                    </tr>
                    <tr>
                      <td>
                        <span className="font-weight-bold d-block">
                          <Trans>Overdue</Trans>
                        </span>
                        {formatAccountCurrency(i.balanceDue)}
                      </td>
                      <td>
                        <span className="font-weight-bold d-block">
                          <Trans>Current</Trans>
                        </span>
                        {formatAccountCurrency(i.balanceDue)}
                      </td>
                      <td>
                        <span className="font-weight-bold d-block">
                          <Trans>Total {{ currency: i.currency }} Due</Trans>
                        </span>
                        {formatAccountCurrency(i.balanceDue)}
                      </td>
                    </tr>
                    <tr>
                      <td className="font-weight-bold">
                        <Trans>Amount Enclosed</Trans>
                      </td>
                      <td
                        colSpan="3"
                        style={{ borderBottom: "1px solid black" }}
                      />
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          </footer>
        </div>
      ))}
    </div>
  </DocumentRenderer>
);

const enhance = compose(
  withSelectedOrganisation(),
  withProps((parentProps) => ({ parentProps })),
  withOrganisationContextConsumer,
  (BaseComponent) =>
    class extends React.Component {
      state = null;
      componentDidMount = () => {
        const { fetchSettings, fetchOrganisation } = this.props;

        Promise.props({
          settings: fetchSettings(),
          organisationData: fetchOrganisation(),
        }).then((state) => this.setState(state));
      };

      render = () => {
        return this.state ? (
          <BaseComponent {...this.props.parentProps} {...this.state} />
        ) : null;
      };
    }
);

export default enhance(StatementDocument);
